import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText,
  Snackbar
} from '@mui/material';
import { Delete, Edit, Preview, Close as CloseIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import ContentDisplay from './display/ContentDisplay';
import ImageUpload from './admin/ImageUpload';
import { handleApiError, handleError } from '../utils/errorHandler';

const PromotionManager = () => {
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [editingPromo, setEditingPromo] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPromo, setPreviewPromo] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [newPromo, setNewPromo] = useState({
    title: '',
    description: '',
    imageUrl: '',
    images: [],
    redirectUrl: '',
    position: 'top', // Set default position
    isActive: true,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    backgroundColor: '#2196f3',
    textColor: '#ffffff',
    buttonText: 'Learn More'
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch all promotions
  const { data: promos } = useQuery({
    queryKey: ['promotions'],
    queryFn: async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) throw new Error('Unauthorized');
      const response = await axios.get('/api/promotions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  });

  const validateForm = (data) => {
    const errors = {};
    
    if (!data.title?.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!data.position?.trim()) {
      errors.position = 'Position is required';
    }

    if (!data.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!data.endDate) {
      errors.endDate = 'End date is required';
    }

    console.log('Validation errors:', errors);
    return errors;
  };

  // Add promotion mutation
  const addPromo = useMutation({
    mutationFn: async (promoData) => {
      const token = localStorage.getItem('adminToken');
      if (!token) throw new Error('Unauthorized');

      console.log('Adding promotion with data:', promoData);
      
      try {
        // Handle image uploads to Cloudinary first
        const imageUploadPromises = promoData.images.map(async (image) => {
          if (image instanceof File) {
            const imageFormData = new FormData();
            imageFormData.append('file', image);
            imageFormData.append('upload_preset', import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET);
            imageFormData.append('folder', 'moviehub/promotions'); // Add folder path for promotion images
            
            const uploadRes = await fetch(
              `https://api.cloudinary.com/v1_1/${import.meta.env.VITE_CLOUDINARY_CLOUD_NAME}/image/upload`,
              {
                method: 'POST',
                body: imageFormData
              }
            );

            if (!uploadRes.ok) {
              throw new Error('Failed to upload image');
            }

            const responseData = await uploadRes.json();
            console.log('Cloudinary response:', responseData);
            return responseData.secure_url;
          }
          return image;
        });

        const uploadedImages = await Promise.all(imageUploadPromises);
        console.log('Uploaded images:', uploadedImages);
        
        // Create promotion data as a regular object
        const promotionData = {
          ...promoData,
          imageUrl: uploadedImages[0] || '', // Set the first image as imageUrl
          images: uploadedImages,
          buttonText: promoData.buttonText // Keep the user-entered button text
        };

        console.log('Sending promotion data:', promotionData);

        const response = await axios.post('/api/promotions', promotionData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Promotion added successfully:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error adding promotion:', error.response?.data || error.message);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      resetForm();
      setSnackbar({
        open: true,
        message: 'Promotion added successfully',
        severity: 'success'
      });
    }
  });

  // Update promotion mutation
  const updatePromo = useMutation({
    mutationFn: async (promoData) => {
      const token = localStorage.getItem('adminToken');
      if (!token) throw new Error('Unauthorized');

      // Handle image uploads first
      const imageUploadPromises = promoData.images.map(async (image) => {
        if (image instanceof File) {
          const imageFormData = new FormData();
          imageFormData.append('file', image);
          imageFormData.append('upload_preset', import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET);
          imageFormData.append('folder', 'moviehub/promotions'); // Add folder path for promotion images
          
          const uploadRes = await fetch(
            `https://api.cloudinary.com/v1_1/${import.meta.env.VITE_CLOUDINARY_CLOUD_NAME}/image/upload`,
            {
              method: 'POST',
              body: imageFormData
            }
          );

          if (!uploadRes.ok) {
            throw new Error('Failed to upload image');
          }

          const responseData = await uploadRes.json();
          return responseData.secure_url;
        }
        return image;
      });

      const uploadedImages = await Promise.all(imageUploadPromises);

      // Create promotion data
      const promotionData = {
        ...promoData,
        imageUrl: uploadedImages[0] || '',
        images: uploadedImages
      };

      const response = await axios.put(`/api/promotions/${editingPromo._id}`, promotionData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Show success message
      setSnackbar({
        open: true,
        message: 'Promotion updated successfully',
        severity: 'success'
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      setEditingPromo(null);
      setSelectedTab(1);
    }
  });

  // Delete promotion mutation
  const deletePromo = useMutation({
    mutationFn: async (id) => {
      const token = localStorage.getItem('adminToken');
      if (!token) throw new Error('Unauthorized');

      await axios.delete(`/api/promotions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      setSnackbar({
        open: true,
        message: 'Promotion deleted successfully',
        severity: 'success'
      });
      setDeleteConfirmOpen(false);
      setPromoToDelete(null);
    }
  });

  const handleEdit = (promo) => {
    console.log('Editing promotion:', promo);
    setEditingPromo({
      ...promo,
      images: promo.imageUrl ? [promo.imageUrl] : [],
      startDate: promo.startDate ? new Date(promo.startDate).toISOString().split('T')[0] : '',
      endDate: promo.endDate ? new Date(promo.endDate).toISOString().split('T')[0] : '',
      buttonText: promo.buttonText // Use the actual button text from database
    });
    setSelectedTab(0);
  };

  const resetForm = () => {
    setEditingPromo(null);
    setNewPromo({
      title: '',
      description: '',
      imageUrl: '',
      images: [],
      redirectUrl: '',
      position: 'top',
      isActive: true,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      backgroundColor: '#2196f3',
      textColor: '#ffffff',
      buttonText: 'Learn More'
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Submitting form. Current state:', editingPromo ? 'editing' : 'new');
    
    try {
      const currentPromo = editingPromo || newPromo;
      console.log('Current promo data:', currentPromo);

      // Validate form
      const formErrors = validateForm(currentPromo);
      if (Object.keys(formErrors).length > 0) {
        console.error('Form validation errors:', formErrors);
        setErrors(formErrors);
        return;
      }
      
      const promoData = {
        title: currentPromo.title,
        description: currentPromo.description || '',
        position: currentPromo.position,
        redirectUrl: currentPromo.redirectUrl || '',
        isActive: Boolean(currentPromo.isActive),
        images: currentPromo.images || [],
        startDate: currentPromo.startDate ? new Date(currentPromo.startDate).toISOString() : new Date().toISOString(),
        endDate: currentPromo.endDate ? new Date(currentPromo.endDate).toISOString() : new Date().toISOString(),
        backgroundColor: currentPromo.backgroundColor || '#2196f3',
        textColor: currentPromo.textColor || '#ffffff',
        buttonText: currentPromo.buttonText || 'Learn More'
      };

      console.log('Processed promo data:', promoData);

      if (editingPromo) {
        await updatePromo.mutate({ ...promoData, _id: editingPromo._id });
      } else {
        await addPromo.mutate(promoData);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    if (editingPromo) {
      setEditingPromo(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      setNewPromo(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear field error when value changes
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleImageChange = (newImages) => {
    console.log('Image change:', newImages);
    
    if (editingPromo) {
      setEditingPromo(prev => ({
        ...prev,
        images: newImages
      }));
    } else {
      setNewPromo(prev => ({
        ...prev,
        images: newImages
      }));
    }
  };

  const handleImageDelete = async (index) => {
    if (editingPromo) {
      // Get the image being deleted
      const imageToDelete = editingPromo.images[index];
      
      try {
        // If it's a Cloudinary URL, delete from Cloudinary
        if (typeof imageToDelete === 'string' && imageToDelete.includes('cloudinary')) {
          const publicId = imageToDelete.split('/').slice(-1)[0].split('.')[0];
          const token = localStorage.getItem('adminToken');
          
          await axios.post('/api/cloudinary/delete', 
            { public_id: `moviehub/promotions/${publicId}` },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        // Update local state
        const updatedImages = [...editingPromo.images];
        updatedImages.splice(index, 1);
        setEditingPromo({
          ...editingPromo,
          images: updatedImages
        });

        // Show success message
        setSnackbar({
          open: true,
          message: 'Image deleted successfully',
          severity: 'success'
        });

      } catch (error) {
        console.error('Error deleting image:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting image: ' + error.message,
          severity: 'error'
        });
      }
    } else {
      // For new promotions, just update the state
      const updatedImages = [...newPromo.images];
      updatedImages.splice(index, 1);
      setNewPromo({
        ...newPromo,
        images: updatedImages
      });
    }
  };

  const handlePreview = () => {
    console.log('Preview data:', editingPromo || newPromo);
    const currentPromo = editingPromo || newPromo;
    const previewData = {
      ...currentPromo,
      image: currentPromo.images?.[0] instanceof File 
        ? URL.createObjectURL(currentPromo.images[0])
        : currentPromo.images?.[0]
    };
    setPreviewPromo(previewData);
    setPreviewOpen(true);
  };

  const positions = [
    { value: 'top', label: 'Top Banner' },
    { value: 'bottom', label: 'Bottom Banner' },
    { value: 'sidebar', label: 'Sidebar' },
    { value: 'popup', label: 'Popup' }
  ];

  // Preview Dialog for Popup
  const PreviewDialog = () => (
    <Dialog 
      open={previewOpen} 
      onClose={() => setPreviewOpen(false)}
      maxWidth={previewPromo?.position === 'popup' ? 'md' : false}
      fullWidth={previewPromo?.position === 'popup'}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'transparent',
          boxShadow: 'none'
        }
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box position="relative">
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.6)'
              },
              zIndex: 2
            }}
          >
            <CloseIcon />
          </IconButton>
          <ContentDisplay
            width={previewPromo?.position === 'popup' ? '600px' : '100%'}
            height={previewPromo?.position === 'popup' ? '400px' : 'auto'}
            title={previewPromo?.title}
            subtitle={previewPromo?.description}
            buttonText={previewPromo?.buttonText}
            image={previewPromo?.image}
            onClick={() => {
              if (previewPromo?.redirectUrl) {
                window.open(previewPromo.redirectUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{
              backgroundColor: previewPromo?.backgroundColor || 'transparent',
              color: previewPromo?.textColor || 'white',
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );

  const renderForm = () => {
    const currentPromo = editingPromo || newPromo;

    return (
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Title */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={currentPromo.title}
              onChange={handleChange('title')}
              required
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={currentPromo.description}
              onChange={handleChange('description')}
            />
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Promotion Images
            </Typography>
            <ImageUpload
              images={currentPromo.images}
              onChange={(newImages) => {
                if (editingPromo) {
                  setEditingPromo({ ...editingPromo, images: newImages });
                } else {
                  setNewPromo({ ...newPromo, images: newImages });
                }
              }}
              onDelete={(index) => handleImageDelete(index)}
            />
          </Grid>

          {/* Position */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select
                value={currentPromo.position}
                label="Position"
                onChange={handleChange('position')}
                required
                error={Boolean(errors.position)}
              >
                {positions.map((pos) => (
                  <MenuItem key={pos.value} value={pos.value}>
                    {pos.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.position && <FormHelperText error>{errors.position}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Button Text */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Button Text"
              value={currentPromo.buttonText}
              onChange={handleChange('buttonText')}
              placeholder="Learn More"
            />
          </Grid>

          {/* Redirect URL */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Redirect URL"
              value={currentPromo.redirectUrl}
              onChange={handleChange('redirectUrl')}
            />
          </Grid>

          {/* Active */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={currentPromo.isActive}
                  onChange={handleChange('isActive')}
                />
              }
              label="Active"
            />
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={currentPromo.startDate}
              onChange={handleChange('startDate')}
              InputLabelProps={{ shrink: true }}
              required
              error={Boolean(errors.startDate)}
              helperText={errors.startDate}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={currentPromo.endDate}
              onChange={handleChange('endDate')}
              InputLabelProps={{ shrink: true }}
              required
              error={Boolean(errors.endDate)}
              helperText={errors.endDate}
            />
          </Grid>

          {/* Background Color */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="color"
              label="Background Color"
              value={currentPromo.backgroundColor}
              onChange={handleChange('backgroundColor')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Text Color */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="color"
              label="Text Color"
              value={currentPromo.textColor}
              onChange={handleChange('textColor')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              {editingPromo && (
                <Button 
                  variant="outlined" 
                  onClick={resetForm}
                  color="secondary"
                >
                  Cancel Edit
                </Button>
              )}
              <Button 
                type="submit" 
                variant="contained"
                color="primary"
              >
                {editingPromo ? "Update Promotion" : "Add Promotion"}
              </Button>
              {currentPromo.title && (
                <Button
                  variant="outlined"
                  onClick={handlePreview}
                  startIcon={<Preview />}
                >
                  Preview
                </Button>
              )}
              {errors.submit && <FormHelperText error>{errors.submit}</FormHelperText>}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderPromotionsList = () => {
    if (!promos?.length) {
      return (
        <Typography variant="body1" sx={{ p: 2 }}>
          No promotions found.
        </Typography>
      );
    }

    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        {promos.map((promo) => (
          <Grid item xs={12} sm={6} md={4} key={promo._id}>
            <Card sx={{ height: '100%' }}>
              {promo.imageUrl && (
                <CardMedia
                  component="img"
                  height="140"
                  image={promo.imageUrl}
                  alt={promo.title}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {promo.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {promo.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Position: {positions.find(p => p.value === promo.position)?.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {promo.isActive ? 'Active' : 'Inactive'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start: {new Date(promo.startDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  End: {new Date(promo.endDate).toLocaleDateString()}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <IconButton 
                    onClick={() => handleEdit(promo)}
                    color="primary"
                    title="Edit"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteClick(promo)}
                    color="error"
                    title="Delete"
                  >
                    <Delete />
                  </IconButton>
                  <IconButton
                    onClick={() => handlePreview(promo)}
                    color="info"
                    title="Preview"
                  >
                    <Preview />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleDeleteClick = (promo) => {
    setPromoToDelete(promo);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (promoToDelete) {
      deletePromo.mutate(promoToDelete._id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPromoToDelete(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label={editingPromo ? "Edit Promotion" : "Add Promotion"} />
          <Tab label="View All Promotions" />
        </Tabs>
      </Box>

      {selectedTab === 0 && (
        <Paper sx={{ p: 3 }}>
          {renderForm()}
        </Paper>
      )}

      {selectedTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            All Promotions
          </Typography>
          {renderPromotionsList()}
        </Paper>
      )}

      <PreviewDialog />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Promotion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the promotion "{promoToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default PromotionManager;
